import { NetworkStatus, OperationVariables } from "@apollo/client";
import React, { useCallback } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";
import { SkeletonText } from "../../ui/skeleton";
import {
  Table,
  TableBody,
  TableData,
  TableFooter,
  TableRow,
} from "../../ui/table";
import { Text } from "../../ui/text";
import Show from "../show";
import DataGridHeader from "./header";
import Row from "./row";
import { DataGridProps } from "./types";

const LoaderColumnsFunc = <
  T extends object,
  TData,
  TVariables extends OperationVariables & { after?: string | null },
  F extends object,
  D extends object,
>(
  props: DataGridProps<T, TData, TVariables, F, D>,
) => {
  const { columns, checkbox, skeletonRows = 10 } = props;
  return Array.from({ length: skeletonRows }).map((_, index) => {
    return (
      <TableRow key={index}>
        {checkbox && <TableData style={{ width: 40 }}></TableData>}
        {columns.map((col) => {
          return (
            <TableData
              key={col.id}
              style={{
                width: col.width ?? undefined,
                flex: col.width ? undefined : 1,
              }}
            >
              <SkeletonText className="h-8" />
            </TableData>
          );
        })}
      </TableRow>
    );
  });
};

const LoaderColumns = React.memo(
  LoaderColumnsFunc,
) as unknown as typeof LoaderColumnsFunc;

const DataGridFunc = <
  T extends object,
  TData,
  TVariables extends { after?: string | null },
  F extends object,
  D extends object,
>(
  props: DataGridProps<T, TData, TVariables, F, D>,
) => {
  const {
    data,
    networkStatus,
    getId,
    showFooter,
    footerData,
    columns,
    emptyComponent,
    listFooter,
  } = props;
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!props.pageInfo?.hasNextPage || networkStatus !== NetworkStatus.ready)
        return;
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;
      if (
        contentOffset.y + layoutMeasurement.height >=
        contentSize.height - 50
      ) {
        props.fetchMore?.({
          variables: {
            after: props.pageInfo?.endCursor,
          } as Partial<TVariables>,
        });
      }
    },
    [networkStatus, props],
  );
  const showIsEmpty =
    data && data.length === 0 && networkStatus !== NetworkStatus.loading;
  return (
    <ScrollView
      onScroll={onScroll}
      className="overflow-auto"
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
    >
      <Table
        variant={props.variant}
        className="w-full table-fixed relative overflow-x-visible"
      >
        <DataGridHeader {...props} />
        <TableBody>
          <Show
            when={
              ![NetworkStatus.loading, NetworkStatus.setVariables].includes(
                networkStatus ?? NetworkStatus.ready,
              )
            }
            fallback={<LoaderColumns {...props} />}
          >
            {showIsEmpty ? (
              <TableRow>
                <TableData
                  size="lg"
                  colspan={columns.length + (props.checkbox ? 1 : 0)}
                  className={`items-center py-8`}
                  isLastRow
                >
                  {emptyComponent}
                </TableData>
              </TableRow>
            ) : (
              data?.map((item, rowIndex) => (
                <Row
                  key={getId(item)}
                  item={item}
                  rowIndex={rowIndex}
                  totalRows={data?.length ?? 0}
                  isSelected={
                    props.selectionModel?.includes(getId(item)) || false
                  }
                  {...props}
                />
              ))
            )}
          </Show>
          <Show when={networkStatus === NetworkStatus.fetchMore}>
            <LoaderColumns {...props} skeletonRows={1} />
          </Show>
        </TableBody>
        {showFooter && (
          <TableFooter>
            <TableRow>
              {props.checkbox && (
                <TableData
                  className="sticky bottom-0 z-10 border-brand-200 border-t"
                  isSelected
                  isLastRow
                  size={props.size}
                ></TableData>
              )}
              {columns.map((column) => {
                return (
                  <TableData
                    key={column.id}
                    className="sticky bottom-0 z-10 border-brand-200 border-t bg-brand-50 first:border-l last:border-r first:rounded-bl-2xl last:rounded-br-2xl"
                    isLastRow
                    size={props.size}
                  >
                    <Text
                      size="sm"
                      action="secondary"
                      bold
                      numberOfLines={1}
                      isTruncated
                    >
                      {column.getFooterValue?.(footerData)}
                    </Text>
                  </TableData>
                );
              })}
            </TableRow>
          </TableFooter>
        )}
      </Table>
      {listFooter}
    </ScrollView>
  );
};

export const DataGrid = React.memo(DataGridFunc) as typeof DataGridFunc;
